import { message } from "antd";
import dayjs from "dayjs";

export function getTimeFromDate(timeString) {
  const hour = dayjs(timeString, { format: "HH:mm:ss" }).format("HH:mm:ss");
  return hour;
}

export function dateMask(value) {
  // Remove any non-numeric characters
  value = value.replace(/\D/g, "");

  // Apply mask as user types
  let maskedValue = "";
  if (value.length <= 2) {
    maskedValue = value.replace(/^(\d{2})/, "$1/");
  } else if (value.length <= 4) {
    maskedValue = value.replace(/^(\d{2})(\d{0,2})/, "$1/$2");
  } else if (value.length <= 8) {
    maskedValue = value.replace(/^(\d{2})(\d{2})(\d{0,4})/, "$1/$2/$3");
  } else {
    maskedValue = value
      .slice(0, 8)
      .replace(/^(\d{2})(\d{2})(\d{4})/, "$1/$2/$3");
  }

  return maskedValue;
}

export function formatToBRL(value) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(Number(value));
  // .slice(3);
}

// Abre a URL atual numa janela nova maximizada, sem o header/menu do app
// (ver isPopupWindow em src/layout/AppLayout.js) — usado pelos editores de
// notícias/análise pra ganhar área útil de edição.
export const openPopupEditor = () => {
  const url = new URL(window.location.href);
  url.searchParams.set("popup", "1");
  const { availWidth, availHeight } = window.screen;
  window.open(
    url.toString(),
    "_blank",
    `popup=1,menubar=no,toolbar=no,location=no,status=no,width=${availWidth},height=${availHeight}`
  );
};

// Abre uma página de relatório (Comex/USDA/Central de Gráficos) numa janela
// popup e "escuta" o botão de copiar/gerar embed que ela já tem, sem precisar
// mexer em nenhuma dessas páginas: intercepta navigator.clipboard.writeText
// dentro da janela (mesma origem) e repassa o texto copiado pro onCapture,
// preservando o comportamento normal de cópia (o .then()/toast da página
// continua funcionando, só que também entregamos o HTML pro artigo).
export const openReportPopupAndCapture = (path, onCapture) => {
  const url = new URL(path, window.location.origin);
  url.searchParams.set("popup", "1");

  const popup = window.open(
    url.toString(),
    "_blank",
    "popup=1,menubar=no,toolbar=no,location=no,status=no,width=1400,height=900"
  );

  if (!popup) {
    message.error("Não foi possível abrir a janela. Verifique o bloqueador de pop-ups.");
    return;
  }

  try {
    const originalWriteText = popup.navigator.clipboard.writeText.bind(popup.navigator.clipboard);
    popup.navigator.clipboard.writeText = (text) => {
      try {
        onCapture(text);
      } catch (err) {
        console.error("Erro ao capturar gráfico copiado:", err);
      }
      return originalWriteText(text);
    };
  } catch (err) {
    console.error("Não foi possível conectar à janela do relatório:", err);
  }
};

// Cola de roteiro/dossiê dentro do próprio Jodit costuma virar um
// <p><span>frase</span></p> por linha — cada <p> tem margem própria, então
// vira um espaçamento vertical enorme entre frases curtas que deveriam só
// ser quebras de linha. Intercepta o HTML bem antes de ser inserido (evento
// "beforePasteInsert" do Jodit — não mexe no comportamento do Enter, que é
// o que quebrava a remoção de listas quando tentamos antes) e, só quando o
// texto colado é claramente uma sequência de linhas curtas (>=5 parágrafos
// seguidos, sem nada além de <p> misturado — tabela/imagem/lista não entra
// nessa regra), mescla tudo num único parágrafo com <br> entre as linhas.
export const limparColagemDeLinhas = (htmlColado) => {
  if (!htmlColado || typeof htmlColado !== "string") return htmlColado;

  const container = document.createElement("div");
  container.innerHTML = htmlColado;

  // Desembrulha <span> sem nenhum atributo (não carregam estilo nenhum,
  // só poluem o HTML) — feito sempre, independente de mesclar ou não.
  container.querySelectorAll("span").forEach((span) => {
    if (span.attributes.length === 0) {
      while (span.firstChild) {
        span.parentNode.insertBefore(span.firstChild, span);
      }
      span.remove();
    }
  });

  const filhos = Array.from(container.children);
  const paragrafos = filhos.filter((el) => el.tagName === "P");

  if (paragrafos.length >= 5 && paragrafos.length === filhos.length) {
    const linhas = paragrafos
      .map((p) => p.innerHTML.trim())
      .filter((linha) => linha.length > 0);
    return `<p>${linhas.join("<br>")}</p>`;
  }

  return container.innerHTML;
};

// Contrapartida do removerParagrafos: prepara o HTML salvo (linhas separadas
// por <br>) para entrar no Jodit.
//
// Sem isto o ciclo abrir→salvar era assimétrico e duplicava as quebras a cada
// volta: ao receber "A<br>B" o Jodit embrulha cada linha solta num <p> mas
// deixa o <br> ali como irmão ("<p>A</p><br><p>B</p>"); no save o </p> virava
// mais um <br>, e o texto voltava pro banco com um <br> a mais que antes —
// 1, 2, 3, 4... sem parar. Aqui as linhas viram <p> de verdade e os <br> que
// as separavam somem, então o que o Jodit devolve no save é igual ao que
// entrou. Feito no DOM, e não por regex, pra só tocar nos <br> de primeiro
// nível: os que estão dentro de lista, tabela ou parágrafo ficam intactos.
export const normalizarParaEditor = (html) => {
  if (!html || typeof html !== "string") return html;

  const BLOCOS =
    /^(P|DIV|UL|OL|LI|TABLE|THEAD|TBODY|TR|TD|TH|H1|H2|H3|H4|H5|H6|BLOCKQUOTE|FIGURE|FIGCAPTION|PRE|HR|IFRAME|VIDEO|SECTION|ARTICLE)$/;

  const container = document.createElement("div");
  container.innerHTML = html;

  const saida = document.createElement("div");
  let buffer = [];

  const temConteudo = (el) =>
    el.textContent.trim() !== "" || el.querySelector("img,iframe,video,table") !== null;

  // Fecha a linha acumulada. Devolve false quando não havia nada nela.
  const fecharLinha = () => {
    if (!buffer.length) return false;
    const p = document.createElement("p");
    buffer.forEach((no) => p.appendChild(no));
    buffer = [];
    if (!temConteudo(p)) return false;
    saida.appendChild(p);
    return true;
  };

  Array.from(container.childNodes).forEach((no) => {
    if (no.nodeType === Node.ELEMENT_NODE && no.nodeName === "BR") {
      // <br> depois de outro <br> (nada acumulado no meio) é a linha em branco
      // que o redator pediu com Enter duplo — vira o parágrafo vazio, que é
      // como o Jodit representa uma linha em branco.
      if (!fecharLinha() && saida.lastChild) {
        const vazio = document.createElement("p");
        vazio.appendChild(document.createElement("br"));
        saida.appendChild(vazio);
      }
      return;
    }
    if (no.nodeType === Node.ELEMENT_NODE && BLOCOS.test(no.nodeName)) {
      fecharLinha();
      saida.appendChild(no);
      return;
    }
    if (no.nodeType === Node.TEXT_NODE && !no.textContent.trim()) return;
    buffer.push(no);
  });
  fecharLinha();

  // <br> sobrando no fim do conteúdo antigo viraria parágrafo vazio pendurado
  // no rodapé da matéria.
  while (
    saida.lastChild &&
    saida.lastChild.nodeName === "P" &&
    !temConteudo(saida.lastChild)
  ) {
    saida.removeChild(saida.lastChild);
  }

  return saida.innerHTML;
};

// Conta as palavras do texto visível de um HTML (tags, imagens e entidades
// não contam). Mesma regra do servidor (rb_comentario_backup.js na API), que
// é quem decide de verdade se o texto já passou do mínimo para backup.
export const contarPalavras = (html) => {
  if (!html || typeof html !== "string") return 0;
  const texto = html
    .replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&[a-z#0-9]+;/gi, "x");
  const palavras = texto.match(/[0-9A-Za-zÀ-ÿ]+(?:[-'’.,][0-9A-Za-zÀ-ÿ]+)*/g);
  return palavras ? palavras.length : 0;
};

// Remove definitivamente qualquer <p> que ainda tenha sobrado no conteúdo
// (digitado direto, colado em pequena quantidade — abaixo do limite do
// limparColagemDeLinhas — etc.) antes de salvar a matéria. Feito só no
// momento de salvar, nunca durante a edição ao vivo: mexer no "enter" do
// Jodit pra nunca criar <p> quebra a remoção de listas (já tentamos e
// revertemos), então aqui só normalizamos o HTML final, depois que o
// usuário já terminou de editar.
export const removerParagrafos = (html) => {
  if (!html || typeof html !== "string") return html;

  return html
    // Parágrafo vazio é a linha em branco que o redator pediu com Enter duplo,
    // e ela precisa chegar ao ar — é o único recurso de espaçamento que sobra
    // agora que o editor mostra os parágrafos colados (igual ao site). Vira
    // UMA quebra só: somada à quebra da linha anterior, dá a linha em branco.
    // Tratar aqui, antes da regra genérica, é o que impede o espaço de dobrar
    // (o <br> que o Jodit deixa dentro do <p> + o que a troca do </p> gera).
    .replace(/<p[^>]*>(\s|&nbsp;|<br\s*\/?>)*<\/p>/gi, "<br>")
    .replace(/<p[^>]*>/gi, "") // abertura de <p> vira nada
    .replace(/<\/p>/gi, "<br>") // fechamento de <p> vira quebra de linha
    .replace(/^(\s*<br\s*\/?>\s*)+/i, "") // <br> sobrando no começo
    .replace(/(\s*<br\s*\/?>\s*)+$/i, ""); // <br> sobrando no final
};

export const handleCopyText = (text) => {
  try {
    navigator.clipboard.writeText(text);
    message.success("Link copiado para a área de transferência!");
  } catch (err) {
    message.error("Não foi possível concluir a ação!");
    console.log(err);
  }
};
